import PropTypes from "prop-types";
import { CLIENT_SHIFT_TYPE_OPTIONS } from "../../constants/clientPortal";
import { formatGbp } from "../../helpers/currency";
import { calculateRoleRateCardHourly } from "../../helpers/roleRateCardPricing";

function formatPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n}%`;
}

/**
 * Live Admin pricing preview for role create/edit forms.
 * Shows the full calculation for each shift type using global uplifts.
 */
const RolePricingBreakdownPreview = ({
  workerBaseRate,
  marginPercentage,
  uplifts,
}) => {
  const base = parseFloat(workerBaseRate);
  const margin = parseFloat(marginPercentage);
  const hasBase = Number.isFinite(base) && base >= 0;
  const hasMargin = Number.isFinite(margin) && margin >= 0;

  if (!hasBase || !hasMargin || !uplifts) {
    return (
      <div className="role-pricing-preview text-muted small mt-3">
        Enter a worker base rate and margin percentage to see the calculated
        rates for each shift type.
      </div>
    );
  }

  return (
    <div className="role-pricing-preview mt-3">
      <div className="fw-semibold mb-2">Pricing calculation preview</div>
      <p className="text-muted small mb-3 mb-md-2">
        Uses global Settings percentages (holiday pay, uplifts, NI, pension,
        levy). Margin is applied to the final worker rate only.
      </p>
      <div className="role-pricing-preview__grid">
        {CLIENT_SHIFT_TYPE_OPTIONS.map((option) => {
          const calc = calculateRoleRateCardHourly({
            workerBaseRate: base,
            shiftType: option.value,
            uplifts,
            marginPercentage: margin,
          });

          return (
            <div
              key={option.value}
              className="role-pricing-preview__card border rounded p-3"
            >
              <div className="fw-semibold mb-2">{option.label}</div>
              <ul className="list-unstyled small mb-0 role-pricing-preview__rows">
                <li>
                  <span>Basic Worker Rate</span>
                  <strong>{formatGbp(calc.baseRate)}</strong>
                </li>
                <li>
                  <span>
                    Holiday Pay ({formatPct(calc.holidayPayPct)})
                  </span>
                  <strong>{formatGbp(calc.holidayPayAmount)}</strong>
                </li>
                <li>
                  <span>Standard Worker Rate</span>
                  <strong>{formatGbp(calc.rateWithHoliday)}</strong>
                </li>
                <li>
                  <span>
                    Shift Uplift ({formatPct(calc.upliftPercent)})
                  </span>
                  <strong>{formatGbp(calc.upliftAmount)}</strong>
                </li>
                <li>
                  <span>Final Worker Rate</span>
                  <strong>{formatGbp(calc.workerRate)}</strong>
                </li>
                <li>
                  <span>Employer NI ({formatPct(calc.niPct)})</span>
                  <strong>{formatGbp(calc.niAmount)}</strong>
                </li>
                <li>
                  <span>Employer Pension ({formatPct(calc.pensionPct)})</span>
                  <strong>{formatGbp(calc.pensionAmount)}</strong>
                </li>
                <li>
                  <span>Apprenticeship Levy ({formatPct(calc.levyPct)})</span>
                  <strong>{formatGbp(calc.levyAmount)}</strong>
                </li>
                <li>
                  <span>Margin ({formatPct(calc.marginPct)})</span>
                  <strong>{formatGbp(calc.marginAmount)}</strong>
                </li>
                <li className="role-pricing-preview__total">
                  <span>Final Client Rate</span>
                  <strong>{formatGbp(calc.clientHourly)}</strong>
                </li>
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};

RolePricingBreakdownPreview.propTypes = {
  workerBaseRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  uplifts: PropTypes.object,
};

export default RolePricingBreakdownPreview;
